// §34.11 (D-497, D-501) — the calendar's event model and its RRULE subset.
//
// In `shared` because **both halves need it**: the app draws the month, and the kernel's reminder
// scheduler has to know when the next occurrence of a recurring event is (§6.5 forbids `apps/**` from
// importing `kernel/**`, so a model either sits here or gets written twice).
//
// ── Two decisions the rest of the file follows from
//
// **Times are local wall-clock ISO without a zone** (§34.11): `2026-08-10T14:00`. This is a desktop
// calendar, not a scheduling server. An event at 9 am is at 9 am wherever the person is, and storing
// an instant would make a meeting move when they flew somewhere. All-day events carry a date only.
//
// **Expansion is windowed, always** (D-501). `FREQ=WEEKLY` with no `UNTIL` is an infinite set, so
// there is no `occurrences(event)` — only `occurrencesIn(event, from, to)`. The two questions anyone
// asks are "what is on this month" and "what fires soon", and both are windows. A function that
// returned every occurrence would be a hang waiting for a caller to forget a limit.
//
// ── The subset, and the one behaviour that looks like a bug
//
// `FREQ` of four values · `INTERVAL` (default 1) · `UNTIL`. No `BYDAY`, no `COUNT`, no exceptions.
// Weekly recurs on the start's weekday; monthly on the start's day-of-month; yearly on month+day.
//
// **Monthly on the 31st skips short months.** §34.11 states it as behaviour rather than a defect:
// an event on 31 January with `FREQ=MONTHLY` fires in March, May, July… and **not in February, April,
// June**. The alternative — clamping to the last day of the month — sounds friendlier and is worse,
// because it silently turns "the 31st" into "the 30th" for half the year and there is then no way to
// express "the 31st and only the 31st". Date arithmetic that adds months naively gets this wrong in a
// specific, testable way: `new Date(2026, 0, 31)` plus three months normalises to **1 May**, so a
// naive implementation fires in April on the wrong day rather than skipping it. That is the case
// M64.1's truth table exists to pin.

/** The four `FREQ` values §34.11 admits. */
export type RecurFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface Recurrence {
  readonly freq: RecurFreq;
  /** `INTERVAL`. 1 means every period. */
  readonly interval: number;
  /** `UNTIL`, inclusive, as `YYYY-MM-DD`. Absent means forever. */
  readonly until?: string;
}

export interface CalendarEvent {
  readonly id: string;
  readonly title: string;
  /** Local wall-clock ISO: `YYYY-MM-DDTHH:mm`, or `YYYY-MM-DD` when `allDay`. */
  readonly start: string;
  readonly end: string;
  readonly allDay: boolean;
  readonly notes?: string;
  /** A §14-ladder hue, so a chip can be coloured without inventing a palette. */
  readonly color?: number;
  readonly recur?: Recurrence;
  /** Minutes before the start. `0` is "at the time of the event"; absent is no reminder. */
  readonly reminderMin?: number;
}

/** One expanded instance. `date` is the day key it belongs to (§11.10's `dayKeyOf` spelling). */
export interface Occurrence {
  readonly event: CalendarEvent;
  /** `YYYY-MM-DD` — the day the grid puts it on. */
  readonly date: string;
  /** Local wall-clock ISO of this instance's start, and its end. */
  readonly start: string;
  readonly end: string;
}

export interface CalendarFile {
  readonly v: 1;
  readonly events: readonly CalendarEvent[];
}

/** Where the file lives — under the app's own root, so no capability is needed (§9.5). */
export const CALENDAR_PATH = '/Apps/clock/calendar.json';

const pad = (value: number): string => String(value).padStart(2, '0');

export const dateKey = (year: number, month: number, day: number): string =>
  `${year}-${pad(month + 1)}-${pad(day)}`;

/** `YYYY-MM-DD` (and `…THH:mm`) → the calendar fields, without going through a timezone. */
export function parseLocal(value: string): { year: number; month: number; day: number; hour: number; minute: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value.trim());
  if (!match) return null;
  const [, y = '', m = '', d = '', hh, mm] = match;
  const year = Number(y);
  const month = Number(m) - 1;
  const day = Number(d);
  if (month < 0 || month > 11 || day < 1 || day > 31) return null;
  return { year, month, day, hour: hh === undefined ? 0 : Number(hh), minute: mm === undefined ? 0 : Number(mm) };
}

/** The wall-clock string back again. */
export const formatLocal = (
  parts: { year: number; month: number; day: number; hour?: number; minute?: number },
  withTime: boolean,
): string =>
  withTime
    ? `${dateKey(parts.year, parts.month, parts.day)}T${pad(parts.hour ?? 0)}:${pad(parts.minute ?? 0)}`
    : dateKey(parts.year, parts.month, parts.day);

/**
 * The **instant** a wall-clock string falls on, in the reader's own zone.
 *
 * The one place the model touches a timezone, and it is the right place: a reminder has to be handed
 * to the kernel's alarm service as an instant (D-335), and "9 am local" only becomes an instant when
 * somebody's clock is consulted. Everything else in this file is calendar arithmetic.
 */
export function instantOf(value: string): number | null {
  const parts = parseLocal(value);
  if (parts === null) return null;
  return new Date(parts.year, parts.month, parts.day, parts.hour, parts.minute, 0, 0).getTime();
}

/** How long an event lasts, in minutes of wall clock — preserved by every occurrence. */
function durationMinutes(event: CalendarEvent): number {
  const from = parseLocal(event.start);
  const to = parseLocal(event.end);
  if (from === null || to === null) return 0;
  const a = Date.UTC(from.year, from.month, from.day, from.hour, from.minute);
  const b = Date.UTC(to.year, to.month, to.day, to.hour, to.minute);
  return Math.max(0, Math.round((b - a) / 60_000));
}

/** Add `minutes` of wall clock to a parsed start, as calendar fields. */
function addMinutes(
  parts: { year: number; month: number; day: number; hour: number; minute: number },
  minutes: number,
): { year: number; month: number; day: number; hour: number; minute: number } {
  const shifted = new Date(parts.year, parts.month, parts.day, parts.hour, parts.minute + minutes);
  return {
    year: shifted.getFullYear(),
    month: shifted.getMonth(),
    day: shifted.getDate(),
    hour: shifted.getHours(),
    minute: shifted.getMinutes(),
  };
}

/**
 * Every occurrence of `event` whose **start day** falls in `[from, to]`, both `YYYY-MM-DD` inclusive.
 *
 * A non-recurring event is its single instance, included when its start day is in range. A recurring
 * one is walked period by period from its own start; `cap` bounds the walk so a pathological interval
 * or a corrupt record cannot spin.
 */
export function occurrencesIn(
  event: CalendarEvent,
  from: string,
  to: string,
  cap = 512,
): Occurrence[] {
  const start = parseLocal(event.start);
  if (start === null) return [];
  const minutes = durationMinutes(event);
  const withTime = !event.allDay;

  const instanceAt = (year: number, month: number, day: number): Occurrence => {
    const at = { year, month, day, hour: start.hour, minute: start.minute };
    const finish = addMinutes(at, minutes);
    return {
      event,
      date: dateKey(year, month, day),
      start: formatLocal(at, withTime),
      end: formatLocal(finish, withTime),
    };
  };

  if (!event.recur) {
    const key = dateKey(start.year, start.month, start.day);
    return key >= from && key <= to ? [instanceAt(start.year, start.month, start.day)] : [];
  }

  const interval = Math.max(1, Math.floor(event.recur.interval || 1));
  const until = event.recur.until;
  const out: Occurrence[] = [];

  // **Seek to the window rather than walking to it.** A daily event that began in 2020 is ~2 400
  // periods away from a window in 2026, so iterating from step 0 exhausted `cap` and answered
  // *nothing* — an endless rule became an invisible one. Caught by the "bounded by the window, not by
  // the rule" test, which is the whole point of D-501's windowing. `cap` now bounds the **output**,
  // which is what a caller can reason about, instead of the distance from the event's own start.
  const first = firstStepAtOrAfter(start, event.recur.freq, interval, from);

  for (let step = first; step < first + cap; step += 1) {
    const candidate = nthOccurrence(start, event.recur.freq, interval, step);
    // `null` is the short-month skip: the period exists, this event does not fall in it, and the walk
    // **continues** rather than stopping. Stopping is how a 31st-of-the-month event would vanish after
    // its first February instead of resuming in March.
    //
    // Termination is safe despite the skips because the candidates that are *not* null increase
    // monotonically, so `key > to` is still a correct stop; `cap` bounds the pathological case where
    // an interval is large enough that a long run of periods all skip.
    if (candidate === null) continue;
    const key = dateKey(candidate.year, candidate.month, candidate.day);
    if (until !== undefined && key > until) break;
    if (key > to) break;
    if (key >= from) out.push(instanceAt(candidate.year, candidate.month, candidate.day));
  }
  return out;
}

/**
 * The `step`-th occurrence of a recurrence, or `null` when that period does not contain one.
 *
 * Indexed by step rather than iterated, which is what makes the short-month rule exact: the 31st of
 * `start.month + step * interval` either exists in that month or it does not, and asking
 * `Date` to normalise `new Date(2026, 3, 31)` into 1 May is precisely the bug being avoided.
 */
function nthOccurrence(
  start: { year: number; month: number; day: number },
  freq: RecurFreq,
  interval: number,
  step: number,
): { year: number; month: number; day: number } | null {
  if (step === 0) return { year: start.year, month: start.month, day: start.day };

  switch (freq) {
    case 'daily':
    case 'weekly': {
      // Both are a fixed number of days, so `Date` may normalise freely — there is no ambiguity in
      // "42 days after 31 January". Weekly recurs on the start's weekday because 7 days later is the
      // same weekday, which is why §34.11 needs no `BYDAY` to say so.
      const days = (freq === 'daily' ? 1 : 7) * interval * step;
      const shifted = new Date(start.year, start.month, start.day + days);
      return { year: shifted.getFullYear(), month: shifted.getMonth(), day: shifted.getDate() };
    }

    case 'monthly': {
      const total = start.year * 12 + start.month + interval * step;
      const year = Math.floor(total / 12);
      const month = ((total % 12) + 12) % 12;
      // The skip, stated once: if the month is too short for this day-of-month, this period has no
      // occurrence. Never clamped to the last day — that would turn "the 31st" into "the 30th" for
      // half the year, and leave no way to express the 31st at all.
      return start.day > daysInMonth(year, month) ? null : { year, month, day: start.day };
    }

    case 'yearly': {
      const year = start.year + interval * step;
      // 29 February on a common year is the same skip, one period up.
      return start.day > daysInMonth(year, start.month) ? null : { year, month: start.month, day: start.day };
    }
  }
}

/**
 * The first step index whose period could contain an occurrence on or after `from`.
 *
 * Computed rather than searched, one closed form per `FREQ`. It is allowed to be **one step early** —
 * a monthly rule seeking into a month whose occurrence falls before `from`, say — because the loop
 * filters on the day key anyway; being early costs one wasted iteration, whereas being late would
 * silently drop an occurrence, so the arithmetic floors and clamps at 0 deliberately.
 */
function firstStepAtOrAfter(
  start: { year: number; month: number; day: number },
  freq: RecurFreq,
  interval: number,
  from: string,
): number {
  const target = parseLocal(from);
  if (target === null) return 0;

  switch (freq) {
    case 'daily':
    case 'weekly': {
      const perStep = (freq === 'daily' ? 1 : 7) * interval;
      // Whole local days between the two dates. Built from `Date.UTC` on the calendar fields so a
      // daylight-saving boundary inside the span cannot make the division come out at 89.958 days.
      const days = Math.round(
        (Date.UTC(target.year, target.month, target.day) - Date.UTC(start.year, start.month, start.day)) /
          86_400_000,
      );
      return Math.max(0, Math.floor(days / perStep));
    }

    case 'monthly': {
      const months = target.year * 12 + target.month - (start.year * 12 + start.month);
      return Math.max(0, Math.floor(months / interval));
    }

    case 'yearly': {
      return Math.max(0, Math.floor((target.year - start.year) / interval));
    }
  }
}

/** Days in a month, leap years included. `new Date(y, m + 1, 0)` is the last day of month `m`. */
export const daysInMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate();

/** Expand a whole calendar over a window, sorted by start then title — the order the grid shows. */
export function expandAll(
  events: readonly CalendarEvent[],
  from: string,
  to: string,
): Occurrence[] {
  return events
    .flatMap((event) => occurrencesIn(event, from, to))
    .sort((a, b) => (a.start === b.start ? a.event.title.localeCompare(b.event.title) : a.start.localeCompare(b.start)));
}

/** Occurrences bucketed by day key — what the month grid renders from. */
export function byDate(occurrences: readonly Occurrence[]): Map<string, Occurrence[]> {
  const out = new Map<string, Occurrence[]>();
  for (const occurrence of occurrences) {
    const list = out.get(occurrence.date);
    if (list) list.push(occurrence);
    else out.set(occurrence.date, [occurrence]);
  }
  return out;
}

/**
 * The next reminder instant strictly after `after`, or `null`.
 *
 * Windowed by `horizonDays` (`WB_CAL_REMINDER_HORIZON_DAYS`, §34.16) for D-501's reason: an event with
 * no `UNTIL` has no last occurrence, so "the next reminder" is only answerable inside a horizon. An
 * event whose next reminder is beyond it simply has none *yet* — the scheduler asks again later, which
 * is also what makes a reminder survive a restart without anything being stored twice.
 */
export function nextReminderAt(
  event: CalendarEvent,
  after: number,
  horizonDays: number,
): number | null {
  if (event.reminderMin === undefined) return null;
  const from = new Date(after);
  const to = new Date(after + horizonDays * 86_400_000);
  const window = occurrencesIn(
    event,
    // A day either side of the window: a reminder can precede its occurrence across a midnight, so an
    // event early tomorrow has a reminder that belongs to today.
    dateKey(from.getFullYear(), from.getMonth(), from.getDate() - 1),
    dateKey(to.getFullYear(), to.getMonth(), to.getDate() + 1),
  );
  for (const occurrence of window) {
    const at = instantOf(occurrence.start);
    if (at === null) continue;
    const fire = at - event.reminderMin * 60_000;
    if (fire > after) return fire;
  }
  return null;
}

/** Read a calendar file that arrived from outside this process (it syncs — §7.4). */
export function readCalendarFile(value: unknown): CalendarFile {
  if (typeof value !== 'object' || value === null) return { v: 1, events: [] };
  const raw = (value as { events?: unknown }).events;
  if (!Array.isArray(raw)) return { v: 1, events: [] };
  // A row that does not survive this is dropped rather than repaired: a half-understood event is one
  // that shows on the wrong day or reminds at the wrong time, which is worse than one that is missing.
  return { v: 1, events: raw.map(readEvent).filter((event): event is CalendarEvent => event !== null) };
}

function readEvent(value: unknown): CalendarEvent | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const id = raw['id'];
  const title = raw['title'];
  const start = raw['start'];
  const end = raw['end'];
  if (typeof id !== 'string' || typeof title !== 'string') return null;
  if (typeof start !== 'string' || parseLocal(start) === null) return null;
  if (typeof end !== 'string' || parseLocal(end) === null) return null;
  const recur = readRecurrence(raw['recur']);
  const reminder = raw['reminderMin'];
  const color = raw['color'];
  const notes = raw['notes'];
  return {
    id,
    title,
    start,
    end,
    allDay: raw['allDay'] === true,
    ...(typeof notes === 'string' ? { notes } : {}),
    ...(typeof color === 'number' ? { color } : {}),
    ...(recur === null ? {} : { recur }),
    ...(typeof reminder === 'number' && Number.isFinite(reminder) && reminder >= 0
      ? { reminderMin: Math.floor(reminder) }
      : {}),
  };
}

const FREQS: readonly RecurFreq[] = ['daily', 'weekly', 'monthly', 'yearly'];

function readRecurrence(value: unknown): Recurrence | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const freq = raw['freq'];
  if (typeof freq !== 'string' || !FREQS.includes(freq as RecurFreq)) return null;
  const interval = raw['interval'];
  const until = raw['until'];
  return {
    freq: freq as RecurFreq,
    interval: typeof interval === 'number' && interval >= 1 ? Math.floor(interval) : 1,
    ...(typeof until === 'string' && parseLocal(until) !== null ? { until: until.slice(0, 10) } : {}),
  };
}
